import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { Modal, ModalTitle } from "@/components/Modal";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import { RecordingsPage } from "./RecordingsPage";
import { SshPlayer } from "./SshPlayer";
import type { SessionRecordingSummary } from "./shared";

export function SshRecordings() {
  const { orgId } = useParams();

  return (
    <RecordingsPage
      connectionMethod="SSH"
      label="SSH"
      renderModal={(recording, onClose) =>
        orgId && (
          <ReplayModal orgId={orgId} recording={recording} onClose={onClose} />
        )
      }
    />
  );
}

export function ReplayModal({
  orgId,
  recording,
  onClose,
}: {
  orgId: string;
  recording: Pick<
    SessionRecordingSummary,
    | "id"
    | "resourceName"
    | "requestedByUserName"
    | "requestedByUserEmail"
    | "requestedAt"
    | "truncated"
  >;
  onClose: () => void;
}) {
  const api = useApi();
  const [castFile, setCastFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRecording() {
      const content = await api.getText(
        `/organizations/${orgId}/session-recordings/${recording.id}/content`,
      );

      if (cancelled) return;

      if (!content) {
        setError("Couldn't load the recording.");

        return;
      }

      setCastFile(content);
    }

    void loadRecording();

    return () => {
      cancelled = true;
    };
  }, [api, orgId, recording.id]);

  return (
    <Modal onClose={onClose} maxWidth="max-w-5xl" closeOnBackdropClick={false}>
      <div className="mb-4 shrink-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <ModalTitle>{recording.resourceName}</ModalTitle>
          {recording.truncated && (
            <Chip variant="warning">Incomplete recording</Chip>
          )}
        </div>

        <p className="text-sm text-foreground/70">
          {recording.requestedByUserName ??
            recording.requestedByUserEmail ??
            "Unknown"}{" "}
          · {formatDateTime(recording.requestedAt)}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <SshPlayer castFile={castFile} error={error} />
      </div>

      <div className="mt-4 flex shrink-0 justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
